/**
 * Discriminator-stamping decorator for the tool-error class hierarchy.
 *
 * Every concrete subclass of {@link ToolExecutionError} carries a
 * wire-format discriminator (`errorType`) that the LLM sees on
 * {@link ToolErrorResponse}. Production minification mangles
 * `error.constructor.name`, so the discriminator can't be derived from
 * runtime reflection — it must be a string literal preserved across
 * builds.
 *
 * `@toolError('Foo')` takes the discriminator as an explicit string
 * argument that must match the source-text class name. Bundlers and
 * minifiers rewrite identifiers, not string-literal arguments, so the
 * discriminator survives every transform pass. The class-name match and
 * discriminator uniqueness are enforced as unit-test invariants in
 * `error-type-discriminator.test.ts`.
 */
import type { ToolExecutionError } from './base.ts';

/**
 * Constructor type for any {@link ToolExecutionError} subclass.
 *
 * `abstract new (...args: any[])` is required (not `typeof ToolExecutionError`)
 * because subclasses widen the constructor signature — e.g.
 * `SourceRefTypeMismatchError(column, expectedType, actualType)` — and
 * narrow constructor types are not assignable to wider ones. The
 * `abstract` modifier lets the same constraint cover the abstract branch
 * classes (`DataShapeError`, `EnvironmentError`, …) and the concrete leaves.
 */
// biome-ignore lint/suspicious/noExplicitAny: Constructor parameter widening — see jsdoc above.
type ErrorClass = abstract new (...args: any[]) => ToolExecutionError;

/**
 * Stamp a `static readonly errorType` discriminator onto a
 * {@link ToolExecutionError} subclass.
 *
 * The discriminator is passed as an explicit string argument — it must
 * exactly match the source-text class name. The
 * `error-type-discriminator.test.ts` suite enforces this invariant so
 * typos cannot silently desync the wire format from the class.
 *
 * Apply to every concrete subclass:
 *
 * ```ts
 * @toolError('DatasetNotFoundError')
 * export class DatasetNotFoundError extends ItemNotFoundError { ... }
 * ```
 *
 * Why an explicit string and not `context.name`? Bundler decorator
 * transforms (notably Babel's `@babel/plugin-proposal-decorators`
 * `2023-11`) emit decorated classes as anonymous class expressions and
 * forward the runtime `Function.prototype.name` (which is `''` for an
 * anonymous expression) as `ClassDecoratorContext.name`. An explicit
 * string literal is bundler- and minifier-independent.
 */
export function toolError(errorType: string) {
  return <T extends ErrorClass>(value: T): T => {
    Object.defineProperty(value, 'errorType', {
      value: errorType,
      writable: false,
      configurable: false,
      enumerable: false,
    });
    return value;
  };
}
